// WebSocket Service for Chat in frontend-user-classic
import { io, Socket } from 'socket.io-client'
import { authService } from './authService'
import { resolveUserChatTenantId } from '../utils/tenantUtils'

interface ChatWebSocketEvents {
    message: (message: any) => void
    typing: (data: { conversationId: string; isTyping: boolean }) => void
    read: (data: { conversationId: string }) => void
    error: (error: any) => void
    connected: () => void
    disconnected: () => void
    new_message?: (message: any) => void
    typing_start?: (data: { conversationId: string }) => void
    typing_stop?: (data: { conversationId: string }) => void
}

class ChatWebSocketService {
    private socket: Socket | null = null
    private listeners: Map<string, Set<(...args: any[]) => void>> = new Map()
    private isConnecting = false
    private isAuthenticated = false
    private reconnectAttempts = 0
    private maxReconnectAttempts = Infinity
    private joinedConversationIds: Set<string> = new Set()
    private pendingEmits: Array<{ event: string; data: any }> = []
    private browserListenersAttached = false
    private readonly typingCooldownMs = 1200
    private readonly lastTypingStateByConversation: Map<string, boolean> = new Map()
    private readonly lastTypingSentAtByConversation: Map<string, number> = new Map()
    private readonly recentInboundMessageIds: Map<string, number> = new Map()

    private readonly handleStorageChange = (e: StorageEvent) => {
        if ((e.key === 'accessToken' || e.key === 'token' || e.key === 'user') && !e.newValue && this.socket?.connected) {
            this.disconnect()
        }
    }

    private readonly handleUserKicked = () => {
        this.disconnect()
    }

    private canEmitToChatRoom(): boolean {
        return Boolean(this.socket?.connected && this.isAuthenticated)
    }

    private extractInboundMessageId(data: unknown): string {
        const envelope = data as { message?: Record<string, unknown> }
        const raw = envelope?.message ?? data
        const record = raw as Record<string, unknown> | null
        if (!record || typeof record !== 'object') return ''
        return String(record.messageId ?? record.id ?? record._id ?? '')
    }

    private shouldSkipDuplicateInboundMessage(data: unknown): boolean {
        const messageId = this.extractInboundMessageId(data)
        if (!messageId) return false

        const now = Date.now()
        const seenAt = this.recentInboundMessageIds.get(messageId)
        if (seenAt != null && now - seenAt < 5000) return true

        this.recentInboundMessageIds.set(messageId, now)
        if (this.recentInboundMessageIds.size > 200) {
            for (const [key, at] of this.recentInboundMessageIds) {
                if (now - at > 5000) this.recentInboundMessageIds.delete(key)
            }
        }
        return false
    }

    private enqueueOrEmit(event: string, data: any): void {
        if (this.canEmitToChatRoom()) {
            this.socket?.emit(event, data)
            return
        }

        this.pendingEmits.push({ event, data })
    }

    private flushPendingConversationState(): void {
        if (!this.canEmitToChatRoom()) return

        this.joinedConversationIds.forEach((conversationId) => {
            this.socket?.emit('join_conversation', { conversationId })
        })

        while (this.pendingEmits.length > 0) {
            const next = this.pendingEmits.shift()
            if (!next) break
            this.socket?.emit(next.event, next.data)
        }
    }

    private attachBrowserListeners(): void {
        if (this.browserListenersAttached || typeof window === 'undefined') return
        window.addEventListener('storage', this.handleStorageChange)
        window.addEventListener('user_kicked', this.handleUserKicked)
        this.browserListenersAttached = true
    }

    private detachBrowserListeners(): void {
        if (!this.browserListenersAttached || typeof window === 'undefined') return
        window.removeEventListener('storage', this.handleStorageChange)
        window.removeEventListener('user_kicked', this.handleUserKicked)
        this.browserListenersAttached = false
    }

    connect(): void {
        if (this.socket?.connected || this.isConnecting) {
            return
        }

        const token = authService.getToken()
        if (!token) {
            console.warn('⚠️ [ChatWebSocket] No token available, cannot connect')
            return
        }

        const user = authService.getCurrentUser()
        const tenantId = resolveUserChatTenantId(user)

        this.isConnecting = true

        try {
            this.attachBrowserListeners()
            // Connect to communication-service WebSocket via Vite proxy
            // IMPORTANT: Socket.IO client interprets io('/path') as namespace, not URL path
            // To connect to /communication-service-api/socket.io, we must:
            // 1. Use default namespace '/' (or empty string)
            // 2. Specify path: '/communication-service-api/socket.io'
            const wsUrl = window.location.origin // Use current origin (localhost:5174)
            console.log('🔌 [ChatWebSocket] Connecting to:', wsUrl, 'namespace: / (default)', 'path: /communication-service-api/socket.io', 'tenantId:', tenantId)
            
            // Polling only — WebSocket upgrade fails on production proxy chain.
            this.socket = io(wsUrl, {
                transports: ['polling'],
                upgrade: false,
                path: '/communication-service-api/socket.io', // Full path including service prefix
                reconnection: true,
                reconnectionAttempts: this.maxReconnectAttempts,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                timeout: 20000,
                withCredentials: true,
                // Don't send auth in query - we'll authenticate after connection
            })

            this.socket.on('connect', () => {
                console.log('✅ [ChatWebSocket] Connected, socket ID:', this.socket?.id, 'authenticating...')
                this.isConnecting = false
                this.isAuthenticated = false
                this.reconnectAttempts = 0
                // Authenticate immediately after connection
                this.authenticate()
            })

            this.socket.on('connect_error', (error: any) => {
                console.debug('❌ [ChatWebSocket] Connection error:', error?.message || error)
                this.isConnecting = false
                this.isAuthenticated = false
                this.reconnectAttempts++
                if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                    console.warn('❌ [ChatWebSocket] Max reconnection attempts reached')
                }
            })

            // Listen for authentication success
            this.socket.on('authenticated', (data) => {
                console.log('✅ [ChatWebSocket] Authenticated:', data)
                this.isAuthenticated = true
                this.flushPendingConversationState()
                this.emit('connected')
            })

            this.socket.on('disconnect', (reason) => {
                console.log('🔌 [ChatWebSocket] Disconnected, reason:', reason)
                this.isAuthenticated = false
                this.emit('disconnected')
            })

            // Chat message events - communication-service emits 'message_sent' after sending
            this.socket.on('message', (data) => {
                if (this.shouldSkipDuplicateInboundMessage(data)) return
                this.emit('message', data)
            })

            this.socket.on('message_sent', (data) => {
                // Only forward full message object; new_message carries the actual message to the room
                if (data.message) {
                    if (this.shouldSkipDuplicateInboundMessage(data)) return
                    this.emit('message', data.message)
                }
            })

            this.socket.on('new_message', (data) => {
                if (this.shouldSkipDuplicateInboundMessage(data)) return
                this.emit('message', data.message || data)
            })

            // Typing events - communication-service emits 'user_typing'
            this.socket.on('user_typing', (data) => {
                this.emit('typing', {
                    conversationId: data.conversationId,
                    isTyping: data.isTyping !== false
                })
            })

            this.socket.on('typing', (data) => {
                this.emit('typing', data)
            })

            // Read events
            this.socket.on('messages_read', (data) => {
                this.emit('read', { conversationId: data.conversationId })
            })

            this.socket.on('read', (data) => {
                this.emit('read', data)
            })

            // Conversation events
            this.socket.on('conversation_joined', (data) => {
                console.log('✅ [ChatWebSocket] Joined conversation:', data.conversationId)
            })

            this.socket.on('conversation_left', (data) => {
                console.log('👋 [ChatWebSocket] Left conversation:', data.conversationId)
            })

            // Error events
            this.socket.on('error', (error) => {
                console.error('❌ [ChatWebSocket] Error:', error)
                this.emit('error', error)
            })

            this.socket.on('auth_error', (error) => {
                console.error('❌ [ChatWebSocket] Auth error:', error)
                this.emit('error', error)
            })

            this.socket.on('message_error', (error) => {
                console.error('❌ [ChatWebSocket] Message error:', error)
                this.emit('error', error)
            })

            this.socket.on('conversation_error', (error) => {
                console.error('❌ [ChatWebSocket] Conversation error:', error)
                this.emit('error', error)
            })

        } catch (error) {
            console.error('❌ [ChatWebSocket] Failed to connect:', error)
            this.isConnecting = false
        }
    }

    private authenticate(): void {
        if (!this.socket?.connected) {
            console.warn('⚠️ [ChatWebSocket] Cannot authenticate: socket not connected')
            return
        }

        const user = authService.getCurrentUser()
        const token = authService.getToken()
        const tenantId = resolveUserChatTenantId(user)
        
        // Get user ID - support multiple formats
        const userId = (user as any)?._id || user?.id || (user as any)?.userId
        
        if (!userId || !token) {
            console.error('❌ [ChatWebSocket] Cannot authenticate: missing userId or token', { userId, hasToken: !!token })
            return
        }

        console.log('🔐 [ChatWebSocket] Authenticating...', { userId, userType: 'user', tenantId })
        
        this.socket.emit('authenticate', {
            userId: userId,
            userType: 'user',
            token: token,
            metadata: {
                tenantId: tenantId,
                username: user?.username || ''
            }
        })
    }

    joinConversation(conversationId: string): void {
        if (this.joinedConversationIds.has(conversationId) && this.canEmitToChatRoom()) {
            return
        }
        this.joinedConversationIds.add(conversationId)
        console.log('🔌 [ChatWebSocket] Joining conversation:', conversationId)
        this.enqueueOrEmit('join_conversation', { conversationId })
    }

    leaveConversation(conversationId: string): void {
        this.joinedConversationIds.delete(conversationId)
        if (!this.canEmitToChatRoom()) return
        this.socket?.emit('leave_conversation', { conversationId })
    }

    sendMessage(
        conversationId: string,
        content: string,
        options?: {
            replyTo?: { id?: string; content?: string; senderName?: string }
        },
    ): void {
        this.enqueueOrEmit('send_message', {
            conversationId,
            content: { text: content },
            senderType: 'user',
            ...(options?.replyTo ? { replyTo: options.replyTo } : {}),
        })
    }

    sendTyping(conversationId: string, isTyping: boolean): void {
        const now = Date.now()
        const lastState = this.lastTypingStateByConversation.get(conversationId)
        const lastSentAt = this.lastTypingSentAtByConversation.get(conversationId) || 0
        if (lastState === isTyping && now - lastSentAt < this.typingCooldownMs) {
            return
        }
        this.lastTypingStateByConversation.set(conversationId, isTyping)
        this.lastTypingSentAtByConversation.set(conversationId, now)
        this.enqueueOrEmit(isTyping ? 'typing_start' : 'typing_stop', { conversationId })
    }

    markAsRead(conversationId: string): void {
        this.enqueueOrEmit('mark_read', {
            conversationId,
            readerType: 'user'
        })
    }

    on<K extends keyof ChatWebSocketEvents>(event: K, callback: ChatWebSocketEvents[K]): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set())
        }
        this.listeners.get(event)!.add(callback as (...args: any[]) => void)
    }

    off<K extends keyof ChatWebSocketEvents>(event: K, callback: ChatWebSocketEvents[K]): void {
        const callbacks = this.listeners.get(event)
        if (callbacks) {
            callbacks.delete(callback as (...args: any[]) => void)
        }
    }

    private emit<K extends keyof ChatWebSocketEvents>(event: K, ...args: any[]): void {
        const callbacks = this.listeners.get(event)
        if (callbacks) {
            callbacks.forEach(callback => {
                try {
                    callback(...args)
                } catch (error) {
                    console.error(`❌ [ChatWebSocket] Error in ${event} callback:`, error)
                }
            })
        }
    }

    disconnect(): void {
        if (this.socket) {
            console.log('🔌 [ChatWebSocket] Disconnecting...')
            this.socket.removeAllListeners()
            this.socket.disconnect()
            this.socket = null
        }
        this.detachBrowserListeners()
        this.listeners.clear()
        this.isConnecting = false
        this.isAuthenticated = false
        this.reconnectAttempts = 0
        this.joinedConversationIds.clear()
        this.lastTypingStateByConversation.clear()
        this.lastTypingSentAtByConversation.clear()
        this.recentInboundMessageIds.clear()
        this.pendingEmits = []
        this.emit('disconnected')
    }

    isConnected(): boolean {
        return this.canEmitToChatRoom()
    }
}

export const chatWebSocketService = new ChatWebSocketService()
